/**
 * Krabbelworm, het spelletje in het startmenu.
 *
 * In de code heet het snake, want dat is het spel waar de regels van komen; in
 * beeld heet het Krabbelworm. Wat je eet zijn krabbeltjes: blokjes van vier
 * pixels in vier kleuren uit het palet van het tekendoek, en je ziet ze door de
 * worm heen schuiven voordat hij langer wordt.
 *
 * De spelregels staan in lib/snake.js; hier staat alles wat met het scherm te
 * maken heeft: de klok, de besturing en het tekenen.
 *
 * Het speelveld hangt bewust buiten this.state, in this.spel. Een stap duurt op
 * het hoogste niveau zeventig milliseconden, en bij elke setState loopt de hele
 * renderVals opnieuw voor alle dertig vensters van de app. Dat is veertien keer
 * per seconde werk voor niets, want van een stap verandert er in het scherm
 * niets behalve de vakjes op het doek. Die tekenen we dus rechtstreeks, en de
 * setState houden we voor wat je erbuiten ziet: de score, het niveau en dood.
 */

import { api, asJson } from '../lib/api.js';
import { PB_URL } from '../lib/config.js';
import { PALETTE_HEX } from '../lib/palette.js';
import {
  playWormBonus, playWormDood, playWormHap, playWormSnel, playWormWinst,
  startWormMuziek, stopLijnMuziek, stopWormMuziek
} from '../lib/sound.js';
import * as snake from '../lib/snake.js';
import * as store from '../lib/store.js';

const VELD = snake.VELD;

/**
 * De vier kleuren komen uit het palet van het tekendoek, zodat het spel bij de
 * paal hoort en niet bij een andere app.
 */
const KOP = PALETTE_HEX[2];    // donkerblauw
const ROMP = PALETTE_HEX[4];   // groen
const HAP = PALETTE_HEX[5];    // rood
const BONUS = PALETTE_HEX[6];  // oranje
const SNEL = PALETTE_HEX[7];   // lichtblauw, het rondje en de snelle worm

/** De collectie die er op de server bij moet; zie CLAUDE.md, sectie 15. */
const COL = 'paal_snake';

export default {
  openSnake() {
      // De slang staat er al klaar onder de startknop, zoals op de Nokia. Een leeg
      // wit veld met een knop erop zegt nog niet dat dit een spelletje is.
      this.spel = snake.nieuwSpel();
      this.setState({ dialog: 'snake', notifOpen: false, startOpen: false, snakeLoopt: false, snakePauze: false, snakeDood: false, snakeVol: false, snakeScore: 0, snakeNiveau: 1 });
      this.snakeBest();
      this.fetchSnakeScores();
      // Het doek is er pas na de render van dit venster.
      setTimeout(() => this.snakeTeken(), 0);
    },

  closeSnake() {
      this.snakeStop();
      stopWormMuziek();
      this.spel = null;
      this.setState({ dialog: null, snakeLoopt: false, snakeDood: false, snakeVol: false, snakeScore: 0, snakeNiveau: 1 });
    },

  /** Je eigen record staat in deze browser, ook als de server niets weet. */
  snakeBest(score) {
      let best = 0;
      try { best = Number(store.get('gp_snake_best') || 0) || 0; } catch (e) {}
      if (score && score > best) {
        best = score;
        try { store.set('gp_snake_best', String(score)); } catch (e) {}
      }
      this.setState({ snakeBest: best });
      return best;
    },

  /**
   * Een beurt beginnen, eventueel meteen in een richting.
   *
   * Start je met het pijltje omhoog, dan hoort de worm ook omhoog te gaan; anders
   * moet je na het starten nog een keer tikken en dat voelt alsof de eerste tik
   * niet aankwam. Naar links kan niet, want de worm ligt met zijn kop naar rechts
   * en dat zou omkeren zijn; draai() weigert dat, dus dan begint hij gewoon naar
   * rechts.
   */
  snakeStart(dx, dy) {
      this.snakeStop();
      // Ligt er al een onaangeroerd veld van openSnake, dan spelen we dat; anders
      // is de vorige beurt afgelopen en komt er een nieuw veld.
      if (!this.spel || this.spel.dood || this.spel.vol || this.spel.tik) this.spel = snake.nieuwSpel();
      if (dx || dy) this.spel = snake.draai(this.spel, dx, dy);
      this.setState({ snakeLoopt: true, snakePauze: false, snakeDood: false, snakeVol: false, snakeScore: 0, snakeNiveau: 1 });
      this.snakeTeken();
      this.spelMuziek();
      this.snakeKlok();
    },

  snakeStop() {
      clearTimeout(this.snakeTimer);
      this.snakeTimer = null;
    },

  /**
   * De klok is een keten van timeouts en geen interval, want het tempo verandert
   * onder het spelen door: bij een interval zou de eerstvolgende stap nog op het
   * oude tempo staan.
   */
  snakeKlok() {
      this.snakeStop();
      if (!this.spel || this.spel.dood || this.spel.vol) return;
      // tempoVan en niet tempo: die kent ook het blauwe rondje, dat het tempo
      // tijdelijk halveert. Elke stap vraagt het opnieuw, dus het versnelt en
      // vertraagt vanzelf.
      const ms = snake.tempoVan(this.spel);
      this.snakeTimer = setTimeout(() => this.snakeTik(), ms);
    },

  snakeTik() {
      // Staat het venster niet meer open, dan is het spel voorbij: klok uit en
      // deuntje uit. Dat kan ook zonder het kruisje, bijvoorbeeld als de
      // adresbalk naar een ander scherm wijst; dan bleef het deuntje anders
      // doorspelen over een venster dat er niet meer is.
      if (!this.spel || this.state.dialog !== 'snake') { this.snakeStop(); stopWormMuziek(); return; }
      // Staat de app op de achtergrond, dan tikt hij niet door; anders lig je
      // dood in je eigen staart zodra je terugkomt.
      if (this.state.snakePauze || (typeof document !== 'undefined' && document.hidden)) {
        this.snakeTimer = setTimeout(() => this.snakeTik(), 200);
        return;
      }
      const was = this.spel;
      const nu = snake.stap(was);
      this.spel = nu;
      this.snakeTeken();

      if (nu.score !== was.score) {
        // Welk geluid hoort erbij? Wat er van het veld verdween zegt het.
        if (nu.happen !== was.happen) this.spelGeluid(playWormHap);
        else if (was.bonus && !nu.bonus) this.spelGeluid(playWormBonus);
        else if (was.snel && !nu.snel) this.spelGeluid(playWormSnel);
        this.setState({ snakeScore: nu.score, snakeNiveau: snake.niveauVan(nu.happen) });
      }
      if (nu.dood || nu.vol) { this.snakeAf(); return; }
      this.snakeKlok();
    },

  /** Dood of veld vol: klok uit, score bewaren, en het venster laat het zien. */
  snakeAf() {
      this.snakeStop();
      const score = this.spel ? this.spel.score : 0;
      stopWormMuziek();
      this.spelGeluid(this.spel && this.spel.vol ? playWormWinst : playWormDood);
      this.setState({ snakeLoopt: false, snakeDood: !!(this.spel && this.spel.dood), snakeVol: !!(this.spel && this.spel.vol), snakeScore: score });
      this.snakeBest(score);
      this.snakeStuurScore(score);
    },

  toggleSnakePauze() {
      if (!this.spel || this.spel.dood || this.spel.vol) return;
      const pauze = !this.state.snakePauze;
      this.setState({ snakePauze: pauze });
      // In de pauze zwijgt het deuntje ook; anders speelt het door over een
      // stilstaand veld en dan lijkt het alsof het spel nog loopt.
      if (pauze) stopWormMuziek(); else this.spelMuziek();
    },

  /** Het deuntje aanzetten, tenzij het geluid uit staat. */
  spelMuziek() {
      if (this.state.spelStil) { stopWormMuziek(); return; }
      startWormMuziek(() => snake.niveauVan(this.spel ? this.spel.happen : 0));
    },

  /** Eén geluidje, tenzij het geluid uit staat. */
  spelGeluid(doe) {
      if (!this.state.spelStil) doe();
    },

  /**
   * Het geluid aan of uit, en dat blijft zo. Muziek die je niet uit kunt zetten
   * is op een kantoor met vierentwintig mensen geen feature.
   */
  toggleSpelStil() {
      const stil = !this.state.spelStil;
      this.setState({ spelStil: stil });
      try { store.set('gp_spel_stil', stil ? '1' : '0'); } catch (e) {}
      if (stil) { stopWormMuziek(); stopLijnMuziek(); return; }
      if (this.state.snakeLoopt && !this.state.snakePauze) this.spelMuziek();
      this.ketMuziek();
    },

  snakeDraai(dx, dy) {
      if (!this.spel) return;
      if (this.state.snakePauze) this.setState({ snakePauze: false });
      this.spel = snake.draai(this.spel, dx, dy);
    },

  /**
   * Pijltjes en WASD. Levert true als de toets van het spel was, zodat de rest
   * van handleKey er niet ook nog iets mee doet.
   */
  snakeKey(e) {
      if (this.state.dialog !== 'snake') return false;
      const k = e.key;
      const paar = /^Arrow(Left)$/.test(k) || k === 'a' ? [-1, 0]
        : /^Arrow(Right)$/.test(k) || k === 'd' ? [1, 0]
        : /^Arrow(Up)$/.test(k) || k === 'w' ? [0, -1]
        : /^Arrow(Down)$/.test(k) || k === 's' ? [0, 1]
        : null;
      if (paar) {
        if (!this.spel || !this.state.snakeLoopt) this.snakeStart(paar[0], paar[1]);
        else this.snakeDraai(paar[0], paar[1]);
        return true;
      }
      if (e.code === 'Space' || k === ' ') {
        if (!this.state.snakeLoopt) this.snakeStart();
        else this.toggleSnakePauze();
        return true;
      }
      return false;
    },

  /** Vegen over het doek: de langste van de twee richtingen wint. */
  snakeVeegStart(e) {
      const t = e.touches && e.touches[0];
      if (t) this.snakeVeeg = { x: t.clientX, y: t.clientY };
    },

  snakeVeegEind(e) {
      const v = this.snakeVeeg;
      this.snakeVeeg = null;
      const t = e.changedTouches && e.changedTouches[0];
      if (!v || !t) return;
      const dx = t.clientX - v.x, dy = t.clientY - v.y;
      // Onder de twintig pixels is het een tik en geen veeg.
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 20) return;
      const kant = Math.abs(dx) > Math.abs(dy) ? [dx > 0 ? 1 : -1, 0] : [0, dy > 0 ? 1 : -1];
      // Veeg je omhoog om te beginnen, dan begint hij ook omhoog.
      if (!this.spel || !this.state.snakeLoopt) { this.snakeStart(kant[0], kant[1]); return; }
      this.snakeDraai(kant[0], kant[1]);
    },

  /**
   * Het veld op het doek zetten: één canvas-pixel per vakje, door het scherm
   * opgeblazen met image-rendering: pixelated. Zo is een vakje hier precies zo
   * hard en vierkant als een pixel op het tekendoek.
   */
  snakeTeken() {
      const cv = this.snakeRef && this.snakeRef.current;
      if (!cv) return;
      const ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, VELD, VELD);
      const g = this.spel;
      if (!g) return;

      /** Eén pixel, met de rand die doorloopt zoals de worm dat ook doet. */
      const pix = (x, y, kleur) => {
        ctx.fillStyle = kleur;
        ctx.fillRect(((x % VELD) + VELD) % VELD, ((y % VELD) + VELD) % VELD, 1, 1);
      };
      const vak = (cel, kleur) => pix(snake.xVan(cel), snake.yVan(cel), kleur);

      if (g.hap >= 0) vak(g.hap, HAP);
      // De bonus en het rondje knipperen de laatste tien tikken, zodat je ziet
      // dat ze weggaan.
      const bijna = (d) => d.tot - g.tik <= 10 && g.tik % 2;
      if (g.bonus && !bijna(g.bonus)) vak(g.bonus.cel, BONUS);
      if (g.snel && !bijna(g.snel)) vak(g.snel.cel, SNEL);

      /**
       * De kant waar een verdikking op ligt: dwars op de looprichting, en altijd
       * dezelfde kant van het lijf. Dat is de looprichting een kwartslag gedraaid,
       * dus loopt hij naar links dan ligt de bult erboven en loopt hij naar rechts
       * dan eronder. Net als de buik van een slang: die zit ook niet nu eens links
       * en dan weer rechts.
       */
      const dwars = (i) => {
        const [dx, dy] = snake.asVan(g, i);
        return [-dy, dx];
      };

      /**
       * Eén pixel verdikking naast vakje `i`. Twee vakjes achter elkaar maken er
       * een bult van twee breed: dat is wat je ziet als de worm iets doorslikt, en
       * ook wat je ziet als hij zijn kop optrekt om te happen.
       */
      const bult = (i, kleur) => {
        const c = g.slang[i];
        const [ox, oy] = dwars(i);
        pix(snake.xVan(c) + ox, snake.yVan(c) + oy, kleur);
      };

      // De worm zelf, één pixel dik.
      const wormKleur = snake.isSnel(g) ? SNEL : ROMP;
      for (let i = g.slang.length - 1; i >= 0; i--) vak(g.slang[i], wormKleur);

      /**
       * Wat hij doorslikt: een bult van twee vakjes lang die naar de staart trekt.
       *
       * Vanaf het tweede vakje, niet vanaf de kop. Op de stap dat hij hapt zit de
       * bult nog op de kop zelf, en dan werd de voorkant ineens twee pixels hoog:
       * dat leest als een kop die verspringt. Nu komt er eerst een bultje net
       * achter de kop, dat een stap later op volle breedte staat en naar achteren
       * schuift. De kop blijft één pixel.
       */
      for (let i = 1; i < g.slang.length; i++) {
        if (snake.bultOp(g, i)) bult(i, wormKleur);
      }

      /**
       * De kop: één donkere pixel op de voorste plek, en die blijft daar liggen.
       * Hij kwam eerst omhoog op het moment van happen, maar een kop die verspringt
       * maakt het juist onduidelijker waar de worm is; de bult ernaast vertelt al
       * dat hij iets binnen heeft.
       */
      vak(g.slang[0], KOP);
    },

  // ---- de ranglijst -------------------------------------------------------

  async fetchSnakeScores() {
      if (!PB_URL) return;
      try {
        const res = await api(`${PB_URL}/api/collections/${COL}/records?sort=-score&perPage=10`);
        // 404 = de collectie bestaat nog niet, 403 = bestaat wel maar mag niet gelezen worden.
        if (!res.ok) { this.setState({ snakeMissing: true, snakeStatus: res.status }); return; }
        const data = await res.json();
        const top = (data.items || []).map((r) => ({ id: r.authorId || r.id, name: r.name || '', score: Number(r.score) || 0 }));
        this.setState({ snakeTop: top, snakeMissing: false, snakeStatus: 0 });
      } catch (e) {
        this.setState({ snakeMissing: true, snakeStatus: 0 });
      }
    },

  /**
   * Alleen je beste beurt gaat naar de server, en per persoon staat er één
   * regel. Anders is de ranglijst na een week een lijst van tweehonderd
   * pogingen van dezelfde vier mensen.
   */
  async snakeStuurScore(score) {
      const id = this.state.accountId;
      if (!PB_URL || !id || !score) return;
      const naam = this.state.author || '';
      try {
        const zoek = await api(`${PB_URL}/api/collections/${COL}/records?filter=(authorId='${id}')&perPage=1`);
        if (!zoek.ok) { this.setState({ snakeMissing: true, snakeStatus: zoek.status }); return; }
        const mijn = ((await zoek.json()).items || [])[0];
        if (mijn && Number(mijn.score) >= score) return;
        const res = mijn
          ? await api(`${PB_URL}/api/collections/${COL}/records/${mijn.id}`, asJson('PATCH', { score, name: naam }))
          : await api(`${PB_URL}/api/collections/${COL}/records`, asJson('POST', { authorId: id, name: naam, score }));
        if (!res.ok) { this.setState({ snakeMissing: true, snakeStatus: res.status }); return; }
        this.fetchSnakeScores();
      } catch (e) {
        this.setState({ snakeMissing: true, snakeStatus: 0 });
      }
    },

  /**
   * Alles wat het venster nodig heeft.
   *
   * Het doek is 32 bij 32 canvas-pixels groot en wordt door het scherm
   * opgeblazen. De rasterlijntjes komen niet van een vaste pixelmaat maar van
   * honderd gedeeld door tweeëndertig, zodat ze op elke schermbreedte precies op
   * de vakjes vallen; het tekendoek kan dat met een vaste maat doen omdat het
   * zijn eigen breedte toch al uitrekent.
   */
  snakeVals() {
      const mob = this.state.isMobile;
      const ink = 'var(--ink)';
      const klaar = !this.state.snakeLoopt;
      const ik = this.state.accountId || '';

      /**
       * Hoeveel schermpixels één vakje breed is, en altijd een heel getal.
       *
       * Anders klopt het raster niet. Op een telefoon van 375 breed zou het bord
       * 311 pixels krijgen; dat is 9,72 per vakje, en dan is de ene schakel van de
       * slang negen pixels breed en de volgende tien. De rasterlijntjes vallen dan
       * ook tussen twee pixels in en vervagen tot vegen.
       *
       * Staand telt niet alleen de breedte maar ook de hoogte mee, want boven en
       * onder het scherm staat de rest van de console. Dat is een schatting met
       * marge: liever een vakje kleiner dan een venster dat moet scrollen.
       */
      const vak = (() => {
        const iw = (typeof window !== 'undefined' && window.innerWidth) || 375;
        const ih = (typeof window !== 'undefined' && window.innerHeight) || 700;
        // In de breedte: de marge van de laag, de rand van het venster en de
        // binnenruimte, aan beide kanten, plus de behuizing eromheen.
        // Precies natellen wat er naast het scherm staat: de marge van .gp-laag,
        // de rand van het venster, de binnenruimte van het paneel, de rand en de
        // binnenruimte van de behuizing, en de rand van het scherm zelf. Op een
        // telefoon van 375 scheelt dat een heel vakje: met een ruwe schatting
        // bleef er acht pixels per vakje over in plaats van negen.
        const buiten = mob ? (20 + 8 + 24 + 6 + 20 + 8) : (40 + 8 + 48 + 6 + 28 + 8);
        const breed = Math.floor((iw - buiten) / VELD);
        // In de hoogte: alles wat er boven en onder het scherm staat. Vierhonderd
        // is ruim gerekend, zodat het venster op een korte laptop niet gaat
        // scrollen; een spelletje waarin je naar de startknop moet zoeken is stuk.
        const hoog = Math.floor((ih - 350) / VELD);
        // Veertien is de bovengrens: het scherm blijft dan onder de 340 en het
        // venster hoeft niet te scrollen, maar een vakje is wel goed te zien.
        return Math.max(7, Math.min(14, breed, hoog));
      })();
      const scherm = vak * VELD;
      /**
       * De bediening staat op een raster van zes kolommen: het pijltjeskruis in de
       * eerste drie, B en A in de laatste twee, en de kolom ertussen leeg. Zo staan
       * alle zes de toetsen op dezelfde lijnen, en is er geen enkele marge met de
       * hand gezet.
       *
       * De kolombreedte volgt de breedte van het scherm, want de rij mag daar niet
       * buiten vallen. Op een telefoon van 320 blijft er zo 32 pixels per toets
       * over en dat is te klein voor een vinger; daar vervalt de lege kolom en gaan
       * we naar vijf, wat 40 oplevert. Veertig is de ondergrens uit CLAUDE.md
       * sectie 7.
       */
      const rijBreed = scherm + 8;
      /**
       * Het raster rekent in kwart stappen, in beide richtingen: elke rij en elke
       * kolom is een kwart toets plus een kwart kier. Een toets beslaat er vier en
       * gaat linksboven in dat vak staan, waardoor de kier eronder en ernaast uit
       * het raster zelf komt in plaats van uit een gap.
       *
       * Zo kan een toets op een kwart stap staan, en dat is wat A en B doen: een
       * halve stap onder en een kwart stap links van waar het raster ze anders zou
       * zetten. Het pijltjeskruis staat op de hele stappen.
       *
       * De toetsmaat wordt zo afgerond dat toets plus kier op een viervoud
       * uitkomt, anders valt een kwart stap op een halve pixel.
       */
      const maatBij = (n, k) => { const m = Math.floor(rijBreed / n) - k; return m - ((m + k) % 4); };
      // Zes kolommen als de toets dan nog 44 haalt, de ondergrens voor een vinger
      // uit CLAUDE.md sectie 7. Zo niet, dan vervalt de lege kolom tussen het kruis
      // en A en B: op een telefoon van 375 wordt de toets daarmee 52 in plaats van
      // 42, en dat is het verschil tussen mikken en spelen.
      const kolommen = maatBij(6, 6) >= 44 ? 6 : 5;
      const kier = kolommen === 6 ? 6 : 4;
      const knop = maatBij(kolommen, kier);
      const kwart = (knop + kier) / 4;
      // Waar B begint, en A vier kwarten verder. Een kwart naar links van zijn
      // hele plek, maar nooit zo ver dat hij het pijltje naar rechts raakt: dat
      // pijltje eindigt op kolom 12, dus 13 is de vroegste plek die vrij is.
      const abKol = Math.max((kolommen - 2) * 4, 13);
      /**
       * Eén toets op het raster. `roze` maakt er de hoofdactie van; dat zijn hier
       * A en B, want daarmee begin je. De pijltjes zijn wit, zoals elke gewone
       * knop in de app.
       */
      const toets = (teken, label, kol, rij, doe, uit, roze) => ({
        teken, label, uit: !!uit, onClick: doe, klasse: roze ? 'gp-btn pink' : 'gp-btn',
        style: {
          gridColumn: kol + ' / span 4', gridRow: rij + ' / span 4',
          justifySelf: 'start', alignSelf: 'start',
          width: knop + 'px', height: knop + 'px', padding: '0', justifyContent: 'center',
          borderRadius: '12px', fontSize: '15px', fontWeight: 700
        }
      });
      /**
       * Een pijltje. Staat het spel stil, dan begint een tik erop meteen een
       * beurt; dat is sneller dan eerst naar A reiken.
       */
      const pijl = (dx, dy, teken, label, kol, rij) =>
        toets(teken, label, kol, rij, () => (klaar ? this.snakeStart(dx, dy) : this.snakeDraai(dx, dy)), false);
      /**
       * A en B doen hetzelfde: een beurt beginnen, of verder na een pauze. Het spel
       * heeft geen tweede handeling, en op een echte handheld werkt allebei ook.
       * De letter zegt een schermlezer niets, dus de toets heet naar wat hij doet
       * en de letter staat er alleen te kijken.
       */
      const abLabel = this.state.snakePauze ? 'Verder'
        : this.state.snakeDood || this.state.snakeVol ? 'Opnieuw' : 'Start';
      const abDoe = () => (this.state.snakePauze ? this.toggleSnakePauze() : this.snakeStart());
      // Alleen uit terwijl de slang echt loopt: per ongeluk je eigen beurt
      // weggooien is erger dan een toets die even niet kan. In pauze moeten ze het
      // juist wel doen, anders is de spatiebalk de enige weg terug.
      const abUit = !klaar && !this.state.snakePauze;
      return {
        snakeTitel: 'Minigame: Krabbelworm!',
        // Zeventien op een telefoon, net als in elk ander venster; op negentien
        // viel deze titel daar over twee regels.
        snakeTitelStyle: {
          fontSize: mob ? '17px' : '19px', fontWeight: 700, letterSpacing: '-.01em', flex: '1 1 auto'
        },
        spelStil: this.state.spelStil,
        toggleSpelStil: this.toggleSpelStil,
        spelStilTitel: this.state.spelStil ? 'Geluid aanzetten' : 'Geluid uitzetten',
        showSnake: this.state.dialog === 'snake',
        openSnake: this.openSnake,
        closeSnake: this.closeSnake,
        snakeRef: this.snakeRef,
        snakeVeld: VELD,
        snakeVeegStart: this.snakeVeegStart,
        snakeVeegEind: this.snakeVeegEind,
        /**
         * ---- het venster is de behuizing ----
         *
         * Hier lagen twee kaders over elkaar: een wit venster met daarin een lila
         * plaat, elk met een eigen rand en een eigen schaduw van acht. Een scherm
         * in een scherm, en dat is precies wat sectie 5 van CLAUDE.md verbiedt
         * ("geen diepte in diepte"). Nu is de lila plaat het venster zelf: één
         * rand, één schaduw, en binnenin het witte schermpje en de knoppen.
         *
         * Lila en niet wit, want die lichte paarse plaat is wat een handheld van
         * dit ding maakt. De vensterrand van vier en de ronding van 24 horen bij
         * een venster; die van de plaat waren drie en 20.
         */
        snakePanelStyle: {
          width: mob ? '100%' : (scherm + 8 + 28 + 8) + 'px', maxHeight: '90dvh',
          overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain',
          background: 'var(--lila-diep)',
          padding: mob ? '12px' : '14px', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box'
        },
        // De behuizing: een staande handheld. Scherm bovenaan, daaronder de stand,
        // en onderaan het pijltjeskruis met Start ernaast, zoals de voorkant van
        // een Game Boy.
        //
        // In lila, het lichtste paars dat de app heeft. Dit was eerst de room-kleur
        // van het bureaublad, die op een wit venster uit de lucht viel, en daarna
        // het volle paars, dat te hard was om knoppen op te zetten. Op een lichte
        // behuizing kan de schaduw van een knop gewoon in inkt, zoals overal.
        // Geen tweede kader meer, alleen nog de kolom waarin scherm en knoppen
        // onder elkaar staan. De plaat eromheen is het venster geworden.
        snakeConsoleStyle: {
          flex: '0 0 auto', alignSelf: 'center', display: 'flex', flexDirection: 'column', gap: '12px',
          minWidth: '0', boxSizing: 'border-box'
        },
        // Het scherm. De rand van vier telt aan beide kanten mee, dus acht erbij;
        // binnenin blijft er precies 32 keer een heel vakje over. De rand en het
        // raster zijn die van het tekendoek; de schaduw hoort bij de behuizing,
        // want een schermpje ligt verzonken en zweeft niet.
        snakeBoardStyle: {
          position: 'relative', flex: '0 0 auto', alignSelf: 'center', boxSizing: 'border-box',
          width: (scherm + 8) + 'px', height: (scherm + 8) + 'px',
          border: '4px solid ' + ink, borderRadius: '8px',
          backgroundColor: '#fff',
          backgroundImage: 'linear-gradient(to right, rgba(36,27,58,.06) 1px, transparent 1px),linear-gradient(to bottom, rgba(36,27,58,.06) 1px, transparent 1px)',
          backgroundSize: vak + 'px ' + vak + 'px',
          overflow: 'hidden', touchAction: 'none'
        },
        snakeCanvasStyle: {
          position: 'absolute', inset: '0', width: '100%', height: '100%',
          imageRendering: 'pixelated', display: 'block'
        },
        // Over het scherm ligt de stand van zaken, en na een beurt ook de
        // ranglijst. Die stond als los blok onder de console en hing er maar bij;
        // op het scherm van een spel is een lijst met de beste beurten juist
        // precies waar hij hoort, zoals in een speelhal.
        //
        // Voor de eerste beurt ligt er niets: dan zie je de slang klaarliggen, en
        // starten doe je met de knop rechtsonder of met de spatiebalk.
        showSnakeOver: !!(this.state.snakePauze || this.state.snakeDood || this.state.snakeVol),
        showSnakeEind: !!(this.state.snakeDood || this.state.snakeVol),
        // Twee losse plakken onder elkaar: de uitslag en de ranglijst. Dat waren
        // eerst één plak met een kopje ertussen, en dan lijkt de ranglijst bij je
        // eigen beurt te horen terwijl het de stand van iedereen is.
        snakeOverStyle: {
          position: 'absolute', inset: '0', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '9px',
          padding: '10px', boxSizing: 'border-box',
          overflowY: 'auto', overscrollBehavior: 'contain',
          background: 'rgba(255,255,255,.62)'
        },
        snakePlakStyle: {
          flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '6px',
          // Tweederde van de laag, dus een derde smaller dan het schermpje. Allebei
          // dezelfde maat, dus even breed zonder dat er een getal te kiezen valt.
          // De ondergrens van 168 is er voor een telefoon van 320: daar zou 67
          // procent 137 zijn, en dan valt "Game over" op 26 pixels over twee
          // regels.
          width: 'max(67%, 168px)', maxWidth: '100%', padding: '10px 12px', boxSizing: 'border-box',
          background: '#fff', border: '3px solid ' + ink, borderRadius: '12px',
          boxShadow: '4px 4px 0 ' + ink
        },
        snakeOverKop: this.state.snakePauze ? 'Pauze' : this.state.snakeVol ? 'Veld vol!' : 'Game over',
        // Zesentwintig, en niet groter: op een telefoon van 320 is het schermpje 216
        // breed en de plak daarbinnen zo'n 170, en daar moet "Game over" nog in.
        snakeOverKopStyle: { fontSize: '26px', fontWeight: 700, lineHeight: 1.15, textAlign: 'center' },
        // Label en getal apart, zoals in de standregel onder het scherm: het woord
        // grijs, het aantal in inkt. Een kale grijze regel liet het getal weglopen
        // tussen de rest, en dat getal is juist waar je naar kijkt.
        snakeEindRegelStyle: {
          display: 'flex', alignItems: 'baseline', justifyContent: 'flex-start', gap: '6px', flexWrap: 'wrap'
        },
        snakeEindLabel: 'Jouw score:',
        snakeEindLabelStyle: { fontSize: '14px', fontWeight: 600, color: 'var(--tekst-3)' },
        snakeEindScore: String(this.state.snakeScore || 0) + (Number(this.state.snakeScore) === 1 ? ' punt' : ' punten'),
        snakeEindScoreStyle: { fontSize: '14px', fontWeight: 700 },
        // Kruis links, Start rechts, op één rij onder de stand.
        // Het raster waarop de zes toetsen staan: kruis links, B en A rechts.
        snakeBedieningStyle: {
          flex: '0 0 auto', display: 'grid',
          // De laatste halve kolom en rij houden een kier over die er aan de rand
          // niet meer hoeft; daarom staat het blok gecentreerd onder het scherm.
          justifyContent: 'center',
          gridTemplateColumns: 'repeat(' + (kolommen * 4) + ', ' + kwart + 'px)',
          gridTemplateRows: 'repeat(8, ' + kwart + 'px)',
          gap: '0'
        },
        // De standbalk met score, niveau en record stond hier. Die is eruit: op
        // het schermpje speelt het spel en daar hoort geen tabel bij. Je score en
        // je record staan na je beurt op de Game over-plak.
        // Alle nummers zijn kwart stappen, dus vier per hele stap. Het kruis staat
        // op de hele stappen: kolom 1, 5 en 9 en rij 1, 5 en 9.
        snakeToetsen: [
          // Twee rijen in plaats van drie: omlaag zit in het midden van het kruis
          // in plaats van eronder. Dat maakt de console een hele knop minder hoog
          // en het kruis leest nog steeds als een kruis.
          pijl(0, -1, '\u25B2', 'Omhoog', 5, 1),
          pijl(-1, 0, '\u25C0', 'Naar links', 1, 5),
          pijl(0, 1, '\u25BC', 'Omlaag', 5, 5),
          pijl(1, 0, '\u25B6', 'Naar rechts', 9, 5),
          // B naast A, halverwege de twee rijen van het kruis. Ze staan een kwart
          // stap linker dan hun hele plek; op een smal scherm vervalt die kwart,
          // want daar zou B tegen het rechterpijltje aan komen.
          toets('B', abLabel, abKol, 3, abDoe, abUit, true),
          toets('A', abLabel, abKol + 4, 3, abDoe, abUit, true)
        ],
        // De ranglijst, compact genoeg voor het schermpje. Je eigen regel licht op
        // in het groen van de slang en niet in geel: dat geel kwam hier uit de
        // lucht vallen, en groen is de kleur die dit spel al heeft.
        /**
         * De beste beurten, als roze pilletjes.
         *
         * Waren witte vakjes met een rangnummer in grijs, en dat leest als een
         * tabel. Nu: de eerste drie krijgen een medaille zoals op het leaderbord
         * van de app, de rest een nummer, en de pil is roze. Je eigen beurt is de
         * volle roze met witte letters; de rest de zachte roze van de balken boven
         * en onder in het scherm.
         */
        snakeTop: (this.state.snakeTop || []).slice(0, 5).map((r, n) => {
          const ikzelf = r.id === ik;
          const medaille = ['\uD83E\uDD47', '\uD83E\uDD48', '\uD83E\uDD49'][n] || '';
          return {
            plek: medaille || String(n + 1),
            name: r.name || 'Iemand',
            score: String(r.score),
            ikzelf,
            rijStyle: {
              display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 9px', boxSizing: 'border-box',
              border: '2px solid ' + ink, borderRadius: '999px',
              background: ikzelf ? 'var(--roze)' : 'var(--roze-balk)',
              color: ikzelf ? '#fff' : ink
            },
            // Een medaille is een plaatje en een cijfer niet: dat cijfer moet wat
            // kleiner en lichter, anders vecht het met de naam ernaast.
            plekStyle: medaille
              ? { fontSize: '13px', flex: '0 0 auto', lineHeight: 1 }
              : { fontSize: '11px', fontWeight: 700, flex: '0 0 auto', width: '13px', textAlign: 'center', opacity: '.65' },
            naamStyle: { fontSize: '13px', fontWeight: 700, flex: '1 1 auto', minWidth: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            scoreStyle: { fontSize: '13px', fontWeight: 700, flex: '0 0 auto' }
          };
        }),
        // Zonder kopje: de pilletjes met medailles zeggen zelf al dat het een
        // ranglijst is, en op een schermpje van 344 is elke regel die niets
        // toevoegt een regel te veel.
        snakeLijstStyle: { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '0' },
        // Waarom is de lijst leeg? Een 404 en een 403 betekenen iets heel anders:
        // de eerste dat de collectie er niet is, de tweede dat hij er wel is maar
        // dat de regels nog dicht staan. Dat verschil kostte zoekwerk, dus het
        // staat er nu gewoon.
        snakeLijstLeeg: this.state.snakeStatus === 403
          ? 'De ranglijst mag nog niet gelezen worden; de regels van de collectie staan dicht.'
          : this.state.snakeMissing
            ? 'De ranglijst staat nog niet op de server.'
            : 'Nog geen scores.',
        showSnakeLijstLeeg: !(this.state.snakeTop || []).length,
        snakeLeegStyle: { fontSize: '12px', fontWeight: 600, color: 'var(--tekst-3)', lineHeight: 1.4, textAlign: 'center' }
      };
    }
};
